import { z } from "zod";
import { auditLogEntrySchema } from "./audit-log.js";
import { paginationSchema } from "./common.js";

export const reportTypeSchema = z.enum([
  "payroll-summary",
  "site-cost",
  "worker-hours",
  "equipment-usage",
  "overtime",
  "exceptions",
  "geofence-exceptions",
  "audit",
]);

export const reportFiltersSchema = paginationSchema.extend({
  payPeriodId: z.string().uuid().optional(),
  siteId: z.string().uuid().optional(),
  workerId: z.string().uuid().optional(),
  crew: z.string().optional(),
  equipmentId: z.string().uuid().optional(),
  jobCode: z.string().optional(),
  costCode: z.string().optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

export const reportTypeParamSchema = z.object({
  reportType: reportTypeSchema,
});

export const payrollSummaryRowSchema = z.object({
  workerId: z.string().uuid(),
  employeeId: z.string(),
  name: z.string().nullable(),
  payPeriodId: z.string().uuid(),
  payPeriodLabel: z.string(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  premiumPay: z.number().nullable(),
  baseRate: z.number().nullable(),
  grossPay: z.number().nullable(),
  equipmentCost: z.number().nullable(),
  notes: z.string().nullable(),
});

export const siteCostRowSchema = z.object({
  siteId: z.string().uuid(),
  siteCode: z.string(),
  siteName: z.string(),
  jobCode: z.string().nullable(),
  costCode: z.string().nullable(),
  laborHours: z.number(),
  laborGrossPay: z.number().nullable(),
  equipmentHours: z.number(),
  equipmentCost: z.number().nullable(),
  totalEstimatedCost: z.number().nullable(),
});

export const workerHoursRowSchema = z.object({
  workerId: z.string().uuid(),
  employeeId: z.string(),
  name: z.string().nullable(),
  siteId: z.string().uuid(),
  siteCode: z.string(),
  crew: z.string().nullable(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  holidayHours: z.number(),
  totalHours: z.number(),
  entryCount: z.number().int(),
});

export const equipmentUsageRowSchema = z.object({
  id: z.string().uuid(),
  equipmentId: z.string(),
  equipmentName: z.string(),
  equipmentType: z.string(),
  siteCode: z.string(),
  siteName: z.string(),
  workerEmployeeId: z.string(),
  date: z.string(),
  usageHours: z.number(),
  hourlyCost: z.number().nullable(),
  estimatedCost: z.number().nullable(),
  notes: z.string().nullable(),
});

export const overtimeRowSchema = z.object({
  workerId: z.string().uuid(),
  employeeId: z.string(),
  name: z.string().nullable(),
  siteCode: z.string(),
  siteName: z.string(),
  regularHours: z.number(),
  overtimeHours: z.number(),
  doubleTimeHours: z.number(),
  overtimePercent: z.number(),
  overtimePay: z.number().nullable(),
  grossPay: z.number().nullable(),
});

export const exceptionReportRowSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  severity: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]),
  message: z.string(),
  status: z.enum(["OPEN", "RESOLVED", "IGNORED"]),
  targetType: z.string(),
  targetId: z.string().uuid(),
  createdAt: z.string(),
});

export const geofenceExceptionRowSchema = z.object({
  id: z.string().uuid(),
  siteId: z.string().uuid(),
  siteCode: z.string(),
  siteName: z.string(),
  workerId: z.string().uuid().nullable(),
  workerName: z.string().nullable(),
  timeEntryId: z.string().uuid().nullable(),
  eventType: z.string(),
  status: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  accuracyMeters: z.number().nullable(),
  distanceFromSiteMeters: z.number().nullable(),
  overrideReason: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
});

export const reportOptionsSchema = z.object({
  payPeriods: z.array(z.object({ id: z.string().uuid(), label: z.string() })),
  sites: z.array(z.object({ id: z.string().uuid(), code: z.string(), name: z.string() })),
  workers: z.array(
    z.object({ id: z.string().uuid(), employeeId: z.string(), name: z.string() }),
  ),
  crews: z.array(z.string()),
  equipment: z.array(
    z.object({ id: z.string().uuid(), equipmentId: z.string(), name: z.string() }),
  ),
  jobCodes: z.array(z.string()),
  costCodes: z.array(z.string()),
});

export const reportResponseSchema = z.object({
  reportType: reportTypeSchema,
  rows: z.array(z.unknown()),
  canViewPayroll: z.boolean(),
});

export type ReportType = z.infer<typeof reportTypeSchema>;
export type ReportFilters = z.infer<typeof reportFiltersSchema>;
export type PayrollSummaryRow = z.infer<typeof payrollSummaryRowSchema>;
export type SiteCostRow = z.infer<typeof siteCostRowSchema>;
export type WorkerHoursRow = z.infer<typeof workerHoursRowSchema>;
export type EquipmentUsageRow = z.infer<typeof equipmentUsageRowSchema>;
export type OvertimeRow = z.infer<typeof overtimeRowSchema>;
export type ExceptionReportRow = z.infer<typeof exceptionReportRowSchema>;
export type GeofenceExceptionRow = z.infer<typeof geofenceExceptionRowSchema>;
export type ReportOptions = z.infer<typeof reportOptionsSchema>;
export type AuditReportRow = z.infer<typeof auditLogEntrySchema>;
